import React from 'react';
import { Button, DatePicker, Input } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'utils/dayjs';
import s from './s.module.less';
import { ReviewData } from '../../../UploadReviewsListModal/types';

const { TextArea } = Input;

type Props = {
    reviews: ReviewData[];
    onUpdate: (reviews: ReviewData[]) => void;
    uploadedCount?: number; // 已上传的评论条数
    maxUploadLimit?: number; // 最大上传条数限制，默认50
    onValidate?: (isValid: boolean) => void; // 校验回调
};

// 校验错误类型
type ValidationErrors = {
    [index: number]: {
        reviewContent?: boolean; // reviewContent字段是否有错误
        date?: boolean; // date字段是否有错误
    };
};

// 使用forwardRef暴露校验方法
const ManualInputStep = React.forwardRef<
{ validate:() => boolean; },
Props
    >((props, ref) => {
        const { reviews, onUpdate, uploadedCount = 0, maxUploadLimit = 50, onValidate } = props;
        const initialized = React.useRef(false);
        const [validationErrors, setValidationErrors] = React.useState<ValidationErrors>({});
        const [shouldValidate, setShouldValidate] = React.useState(false); // 是否应该显示校验错误

        // 如果没有评论，初始化一个空的评论
        React.useEffect(() => {
            if (!initialized.current && reviews.length === 0) {
                initialized.current = true;
                const newReview: ReviewData = {
                    reviewContent: '',
                    date: '',
                };
                onUpdate([newReview]);
            }
        }, [reviews.length, onUpdate]);

        // 校验函数
        const validateReviews = React.useCallback((): boolean => {
            // 如果没有review，返回false
            if (reviews.length === 0) {
                setValidationErrors({});
                setShouldValidate(true);
                onValidate?.(false);
                return false;
            }

            const errors: ValidationErrors = {};
            let isValid = true;

            // 检查每个review，即使只有一个空的review也会触发校验
            reviews.forEach((review, index) => {
                const hasContentError = !review.reviewContent || !review.reviewContent.trim();
                const hasDateError = !review.date;

                if (hasContentError || hasDateError) {
                    errors[index] = {};
                    if (hasContentError) {
                        errors[index].reviewContent = true;
                        isValid = false;
                    }
                    if (hasDateError) {
                        errors[index].date = true;
                        isValid = false;
                    }
                }
            });

            setValidationErrors(errors);
            setShouldValidate(true);
            onValidate?.(isValid);
            return isValid;
        }, [reviews, onValidate]);

        // 暴露校验方法给父组件
        React.useImperativeHandle(ref, () => ({
            validate: validateReviews,
        }), [validateReviews]);

        // 当输入内容改变时，清除对应字段的校验错误
        const clearFieldError = (index: number, field: 'reviewContent' | 'date') => {
            if (shouldValidate && validationErrors[index]?.[field]) {
                const newErrors = { ...validationErrors };
                if (newErrors[index]) {
                    delete newErrors[index][field];
                    if (Object.keys(newErrors[index]).length === 0) {
                        delete newErrors[index];
                    }
                }
                setValidationErrors(newErrors);
            }
        };

        // 计算剩余可上传条数
        const remainingQuota = maxUploadLimit - uploadedCount;
        // 检查是否已达到最大评论数量（基于剩余配额）
        const isMaxReached = reviews.length >= remainingQuota;

        const handleAddReview = () => {
            // 如果已达到最大数量，不允许添加
            if (isMaxReached) {
                return;
            }
            const newReview: ReviewData = {
                reviewContent: '',
                date: '',
            };
            onUpdate([...reviews, newReview]);
        };

        const handleRemoveReview = (index: number) => {
            const newReviews = reviews.filter((_, i) => i !== index);
            onUpdate(newReviews);
            // 清除被删除项的校验错误
            if (validationErrors[index]) {
                const newErrors = { ...validationErrors };
                delete newErrors[index];
                // 重新索引
                const reindexedErrors: ValidationErrors = {};
                Object.keys(newErrors).forEach((key) => {
                    const oldIndex = parseInt(key, 10);
                    if (oldIndex > index) {
                        reindexedErrors[oldIndex - 1] = newErrors[oldIndex];
                    } else {
                        reindexedErrors[oldIndex] = newErrors[oldIndex];
                    }
                });
                setValidationErrors(reindexedErrors);
            }
        };

        const handleContentChange = (index: number, reviewContent: string) => {
            const newReviews = [...reviews];
            newReviews[index] = {
                ...newReviews[index],
                reviewContent,
            };
            onUpdate(newReviews);
            // 清除该字段的校验错误
            clearFieldError(index, 'reviewContent');
        };

        const handleDateChange = (index: number, date: Dayjs | null) => {
            const newReviews = [...reviews];
            newReviews[index] = {
                ...newReviews[index],
                date: date ? date.format('YYYY-MM-DD') : '',
            };
            onUpdate(newReviews);
            // 清除该字段的校验错误
            clearFieldError(index, 'date');
        };

        // 禁用今天之后的日期
        const disabledDate = (current: Dayjs | null) => {
            if (!current) {
                return false;
            }
            // 禁用今天之后的所有日期
            return current.isAfter(dayjs(), 'day');
        };

        return (
            <div className={s.wrap}>
                <div className={s.content}>
                    {reviews.map((review, index) => (
                        <div key={index} className={s.reviewItem}>
                            <div className={s.number}>{index + 1}</div>
                            <div className={s.fields}>
                                <div className={s.field}>
                                    <label className={s.label}>
                                        Review details
                                    </label>
                                    <TextArea
                                        value={review.reviewContent}
                                        onChange={(e) =>
                                            handleContentChange(
                                                index,
                                                e.target.value,
                                            )
                                        }
                                        placeholder="Enter review details..."
                                        rows={6}
                                        className={`${s.textarea} ${
                                            shouldValidate && validationErrors[index]?.reviewContent
                                                ? s.textareaError
                                                : ''
                                        }`}
                                    />
                                    {shouldValidate && validationErrors[index]?.reviewContent && (
                                        <div className={s.errorMessage}>
                                            This is a required field
                                        </div>
                                    )}
                                </div>
                                <div className={s.dateField}>
                                    <label className={s.label}>Review date</label>
                                    <DatePicker
                                        value={
                                            review.date ? dayjs(review.date) : null
                                        }
                                        onChange={(date) =>
                                            handleDateChange(index, date)
                                        }
                                        format="YYYY-MM-DD"
                                        className={`${s.datePicker} ${
                                            shouldValidate && validationErrors[index]?.date
                                                ? s.datePickerError
                                                : ''
                                        }`}
                                        placeholder="Select a date"
                                        disabledDate={disabledDate}
                                    />
                                    {shouldValidate && validationErrors[index]?.date && (
                                        <div className={s.errorMessage}>
                                            This is a required field
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className={s.deleteBtn}>
                                <Button
                                    type="text"
                                    danger
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                        >
                                            <path
                                                d="M15.8359 5.83333L15.1132 15.9521C15.0509 16.8243 14.3251 17.5 13.4507 17.5H6.55447C5.68007 17.5 4.95434 16.8243 4.89204 15.9521L4.16927 5.83333H7.5026V3.33333C7.5026 2.8731 7.8757 2.5 8.33594 2.5H11.6693C12.1295 2.5 12.5026 2.8731 12.5026 3.33333V5.83333H15.8359Z"
                                                fill="white"
                                            />
                                            <path
                                                d="M15.8359 5.83333L15.1132 15.9521C15.0509 16.8243 14.3251 17.5 13.4507 17.5H6.55447C5.68007 17.5 4.95434 16.8243 4.89204 15.9521L4.16927 5.83333M8.33594 9.16667V14.1667M11.6693 9.16667V14.1667M12.5026 5.83333V3.33333C12.5026 2.8731 12.1295 2.5 11.6693 2.5H8.33594C7.8757 2.5 7.5026 2.8731 7.5026 3.33333V5.83333M3.33594 5.83333H16.6693"
                                                stroke="#EF4444"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    }
                                    onClick={() => handleRemoveReview(index)}
                                    className={s.deleteButton}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <div className={s.addButtonWrap}>
                    <Button
                        type="text"
                        onClick={handleAddReview}
                        disabled={isMaxReached}
                        className={`${s.addButton} ${
                            isMaxReached ? s.addButtonDisabled : ''
                        }`}
                        icon={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                            >
                                <path
                                    d="M10 4V16M4 10H16"
                                    stroke={isMaxReached ? '#6B7280' : '#0E62F4'}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        }
                    >
                        Add new review
                    </Button>
                </div>
            </div>
        );
    });

ManualInputStep.displayName = 'ManualInputStep';

export default ManualInputStep;
